import { useCallback, useState } from 'react';

import { VALIDATION_RULES } from '@/shared/lib/constants';
import { Button } from '@/shared/ui/Button';
import { ContentHeader } from '@/shared/ui/ContentHeader';
import { DescriptionEditModal } from '@/shared/ui/DescriptionEditModal';
import { Icon } from '@/shared/ui/Icon';
import { IconButton } from '@/shared/ui/IconButton';
import { ToggleSwitch } from '@/shared/ui/ToggleSwitch';

import {
  type DagListRunStatus,
  formatDagVersionLabel,
  useDagDashboardDetail,
  useDagOverview,
  useUpdateDagMutation,
} from '@/entities/dag';

import {
  useToggleDagBookmarkMutation,
  useToggleDagSchedulingMutation,
  useTriggerDagMutation,
} from '@/features/dag-list';
import { DagStatusBadge } from '@/features/dag-list/ui/DagStatusBadge';

import styles from './DagDetailPageHeader.module.css';

type DagDetailPageHeaderProps = {
  dagUuid: string;
};

const renderDisplayTitle = (dagOverviewName: string, dagOverviewStatus: DagListRunStatus, deployedVersion?: number) => {
  return (
    <div className={styles.displayTitle}>
      <span>
        {dagOverviewName}
        {deployedVersion && ` & ${formatDagVersionLabel(deployedVersion)}`}
      </span>
      <DagStatusBadge runStatus={dagOverviewStatus} />
    </div>
  );
};

export const DagDetailPageHeader = ({ dagUuid }: DagDetailPageHeaderProps) => {
  const { data: dagOverview } = useDagOverview(dagUuid);
  const { data: dagDashboardDetail } = useDagDashboardDetail(dagUuid);

  const { mutate: toggleDagSchedulingMutation, isPending: isToggleDagSchedulingPending } =
    useToggleDagSchedulingMutation();
  const { mutate: triggerDagMutation, isPending: isTriggeringDagPending } = useTriggerDagMutation();
  const { mutate: toggleDagBookmarkMutation, isPending: isTogglingDagBookmarkPending } = useToggleDagBookmarkMutation();
  const { mutateAsync: updateDagMutation, isPending: isUpdatingDagPending } = useUpdateDagMutation();
  const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false);
  const displayStatus: DagListRunStatus = isTriggeringDagPending ? 'pending' : dagOverview.status;

  const displayTitle = renderDisplayTitle(
    dagOverview.name,
    displayStatus,
    dagDashboardDetail.deployedVersion ?? undefined
  );

  const deployedVersionItem = dagDashboardDetail.versions?.find(
    (version) => version.version === dagDashboardDetail.deployedVersion
  );
  const isDeployedVersionSuccessful = deployedVersionItem?.status === 'success';
  const canToggleScheduling = dagDashboardDetail.hasDeployment && isDeployedVersionSuccessful;

  const handleTriggerClick = useCallback(() => {
    triggerDagMutation({
      dagUuid,
      deployed: true,
    });
  }, [dagUuid, triggerDagMutation]);

  const handleFavoriteClick = useCallback(() => {
    toggleDagBookmarkMutation({ dagUuid });
  }, [dagUuid, toggleDagBookmarkMutation]);

  const handleToggleScheduling = useCallback(
    (checked: boolean) => {
      toggleDagSchedulingMutation({
        dagUuid,
        enabled: checked,
      });
    },
    [dagUuid, toggleDagSchedulingMutation]
  );

  const handleDescriptionEdit = useCallback(() => {
    setIsDescriptionModalOpen(true);
  }, []);

  const handleDescriptionModalClose = useCallback(() => {
    setIsDescriptionModalOpen(false);
  }, []);

  const handleDescriptionSubmit = useCallback(
    async (description: string) => {
      await updateDagMutation({
        dagUuid,
        description,
      });
    },
    [dagUuid, updateDagMutation]
  );

  return (
    <>
      <ContentHeader
        title={displayTitle}
        description={dagOverview.description ?? dagDashboardDetail.description}
        className={styles.header}
        showCopyButton={false}
        descriptionEditable={true}
        onDescriptionEdit={handleDescriptionEdit}
        actions={
          <div className={styles.actionGroup}>
            <IconButton
              onClick={handleFavoriteClick}
              disabled={isTogglingDagBookmarkPending}
              tooltip={dagDashboardDetail.isBookmarked ? '즐겨찾기 해제' : '즐겨찾기'}
              tooltipPlacement="bottom"
              ariaLabel={`${dagUuid} 즐겨찾기 ${dagDashboardDetail.isBookmarked ? '해제' : '설정'}`}>
              <Icon name={dagDashboardDetail.isBookmarked ? 'star-filled' : 'star'} size={16} />
            </IconButton>
            <ToggleSwitch
              checked={dagDashboardDetail.isSchedulingEnabled}
              onChange={handleToggleScheduling}
              loading={isToggleDagSchedulingPending}
              onText="활성"
              offText="비활성"
              showLoadingIcon={false}
              width={77}
              disabled={!canToggleScheduling || isToggleDagSchedulingPending}
            />
            <Button
              className={styles.triggerButton}
              variant="filled"
              prefix={!isTriggeringDagPending && <Icon name="run" size={16} />}
              disabled={isTriggeringDagPending}
              loading={isTriggeringDagPending}
              onClick={handleTriggerClick}
              aria-label={`${dagUuid} 트리거`}>
              <span>트리거</span>
            </Button>
          </div>
        }
      />
      <DescriptionEditModal
        open={isDescriptionModalOpen}
        currentDescription={dagOverview.description ?? dagDashboardDetail.description}
        isLoading={isUpdatingDagPending}
        minLength={VALIDATION_RULES.DESCRIPTION_MIN_LENGTH}
        onClose={handleDescriptionModalClose}
        onSubmit={handleDescriptionSubmit}
      />
    </>
  );
};
